import { FormApi } from 'final-form';
import React, { useState } from 'react';
import { LocalizedSpan, Localizer } from 'services/Localizer';
import FileUploadFields from './FileUploadInfo';

const Dropzone = React.lazy(() => import(/* webpackChunkName: "react-dropzone" */ 'react-dropzone'));

export type FileUploadFormProps<T extends FormValuesWithFileUploadField> = {
	formApi: FormApi<T>;
	// Maximum file size (in bytes) accepted by this FileUploadComponent
	maxSize: number;
	// Accepted file types
	accept: string;
};

/**
 * Represents a successfully uploaded file.
 */
export interface FileUploadResponse {
	originalFileName: string;
	friendlyName: string;
	guid: string;
	fileSize: number;
	checksum: string;
}

export interface FileUploadFormState {
	acceptedFile: File | null;
	rejectedFile: File | null;
	uploadError: string | null;
}

export interface FormValuesWithFileUploadField {
	uploadedFileInfo: FileUploadResponse | null;
}

/**
 * A helper component which lets users upload a single file and add the information to custom form
 */
function FileUpload<T extends FormValuesWithFileUploadField>({ formApi, accept, maxSize }: FileUploadFormProps<T>) {
	const [state, setState] = useState({
		acceptedFile: null,
		rejectedFile: null,
		uploadError: null,
	} as FileUploadFormState);

	/**
	 * onDrop event provided by the Dropzone component.
	 */
	const onDrop = (acceptedFiles, rejectedFiles) => {
		if (acceptedFiles.length > 0) {
			// we only need the first accepted file
			setState({
				acceptedFile: acceptedFiles[0] as File,
				rejectedFile: null,
				uploadError: null,
			});
			return;
		}

		if (rejectedFiles.length > 0) {
			// we only need the first rejected file
			setState({
				...state,
				rejectedFile: rejectedFiles[0] as File,
				acceptedFile: null,
				uploadError: null,
			});
		}
	};

	const fileRemovedHandler = () => {
		setState({ ...state, acceptedFile: null });
	};

	const onUploadError = (error: string) => {
		setState({ ...state, uploadError: error, acceptedFile: null });
	};

	return (
		<React.Suspense fallback={<div>{Localizer('LoadingDropzone')}</div>}>
			<Dropzone onDrop={onDrop} accept={accept} multiple={false} maxSize={maxSize}>
				{({ getRootProps, getInputProps }) => (
					<>
						{!state.acceptedFile ? (
							<>
								<div className="dropzone">
									<div className="dz-default dz-message" {...getRootProps()}>
										<input {...getInputProps()} />
										<LocalizedSpan resourceKey="DropzonePrompt" />
									</div>
								</div>
								{state.uploadError ? (
									<span className="roc-form-suffix roc-form-suffix--error">{state.uploadError}</span>
								) : null}
							</>
						) : state.acceptedFile && !state.rejectedFile ? (
							<div className="container" id="file-preview">
								<FileUploadFields
									file={state.acceptedFile}
									formApi={formApi}
									onRemoveFile={fileRemovedHandler}
									onUploadError={onUploadError}
								/>
							</div>
						) : null}
						{state.rejectedFile ? (
							<div className="container" id="rejected">
								<div className="rejected-file roc-form-suffix roc-form-suffix--error">
									<span>{Localizer('RejectedFileError', state.rejectedFile.name)}</span>
								</div>
							</div>
						) : null}
					</>
				)}
			</Dropzone>
		</React.Suspense>
	);
}

export default FileUpload;
